import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Megaphone, EyeOff, Eye, AlertTriangle, Info, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const LEVELS = [
  { value: "info", label: "お知らせ", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Info },
  { value: "warning", label: "注意", color: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertTriangle },
  { value: "important", label: "重要", color: "bg-red-100 text-red-700 border-red-200", icon: AlertCircle },
];

const getLevel = (v) => LEVELS.find((l) => l.value === v) || LEVELS[0];

export default function MessagesManagement() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({
    level: "info",
    title: "",
    body: "",
    expires_at: "",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/messages`, getAuthHeaders());
      setMessages(Array.isArray(r.data?.messages) ? r.data.messages : []);
    } catch (e) {
      toast.error("メッセージ取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ level: "info", title: "", body: "", expires_at: "" });
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setEditing(m);
    setForm({
      level: m.level || "info",
      title: m.title || "",
      body: m.body || "",
      expires_at: m.expires_at || "",
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("タイトルを入力してください");
      return;
    }
    try {
      if (editing) {
        await axios.put(
          `${API}/admin/messages/${editing.id}`,
          {
            level: form.level,
            title: form.title,
            body: form.body,
            expires_at: form.expires_at || null,
          },
          getAuthHeaders()
        );
        toast.success("メッセージを更新しました");
      } else {
        await axios.post(
          `${API}/admin/messages`,
          {
            level: form.level,
            title: form.title,
            body: form.body,
            expires_at: form.expires_at || null,
          },
          getAuthHeaders()
        );
        toast.success("メッセージを送信しました");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "送信に失敗しました");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/messages/${deleteTarget.id}`, getAuthHeaders());
      toast.success("削除しました");
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      toast.error("削除に失敗しました");
    }
  };

  const toggleVisible = async (m) => {
    try {
      await axios.put(
        `${API}/admin/messages/${m.id}`,
        { is_visible: !(m.is_visible !== false) },
        getAuthHeaders()
      );
      toast.success(m.is_visible !== false ? "非表示にしました" : "表示にしました");
      fetchAll();
    } catch (err) {
      toast.error("更新に失敗しました");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold font-chivo tracking-tight text-slate-800 flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-slate-500" />
            メッセージ管理
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            本部・店舗のダッシュボードトップに表示されるお知らせを管理します。
          </p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="new-message-button"
          className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          新規メッセージ
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border border-slate-200 rounded-sm bg-white">
          メッセージがありません
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((m) => {
            const lv = getLevel(m.level);
            const LIcon = lv.icon;
            const visible = m.is_visible !== false;
            return (
              <div
                key={m.id}
                data-testid={`admin-message-${m.id}`}
                className={`bg-white border rounded-sm shadow-sm overflow-hidden ${
                  visible ? "border-slate-200" : "border-slate-200 opacity-60"
                }`}
              >
                <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <LIcon className="h-5 w-5 mt-0.5 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${lv.color}`}>
                          {lv.label}
                        </span>
                        <span className="font-semibold text-slate-800">{m.title}</span>
                        {!visible && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-600 border border-slate-300">
                            非表示
                          </span>
                        )}
                        {visible && !m.is_active && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-200">
                            期限切れ
                          </span>
                        )}
                      </div>
                      {m.body && (
                        <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap break-words">
                          {m.body}
                        </p>
                      )}
                      <div className="mt-2 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                        <span>送信: {new Date(m.created_at).toLocaleString("ja-JP")}</span>
                        {m.expires_at && <span>期限: {m.expires_at}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      onClick={() => toggleVisible(m)}
                      data-testid={`toggle-visible-${m.id}`}
                      variant="ghost"
                      size="sm"
                      title={visible ? "非表示にする" : "表示にする"}
                      className={visible ? "text-slate-600 hover:bg-slate-100" : "text-emerald-600 hover:bg-emerald-50"}
                    >
                      {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      onClick={() => openEdit(m)}
                      data-testid={`edit-message-${m.id}`}
                      variant="ghost"
                      size="sm"
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => setDeleteTarget(m)}
                      data-testid={`delete-message-${m.id}`}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 作成/編集ダイアログ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "メッセージ編集" : "新規メッセージ"}</DialogTitle>
            <DialogDescription className="text-slate-600">
              本部・店舗・グループ本社のダッシュボードトップに表示されます。表示/非表示は管理者が切り替えできます。
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="msg-level">種別</Label>
              <select
                id="msg-level"
                data-testid="msg-level"
                value={form.level}
                onChange={(e) => setForm({ ...form, level: e.target.value })}
                className="mt-1 h-9 w-full px-3 rounded-sm border border-slate-300 bg-white text-sm"
              >
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="msg-title">タイトル</Label>
              <Input
                id="msg-title"
                data-testid="msg-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                lang="ja"
                style={{ imeMode: "active" }}
                className="mt-1 h-9 rounded-sm"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <Label htmlFor="msg-body">本文（任意）</Label>
              <Textarea
                id="msg-body"
                data-testid="msg-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                lang="ja"
                style={{ imeMode: "active" }}
                rows={4}
                className="mt-1 rounded-sm"
              />
            </div>
            <div>
              <Label htmlFor="msg-expires">有効期限（任意 / 空欄=無期限）</Label>
              <Input
                id="msg-expires"
                data-testid="msg-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className="mt-1 h-9 rounded-sm"
              />
            </div>
            <Button
              type="submit"
              data-testid="msg-submit"
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
            >
              {editing ? "更新" : "送信"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* 削除確認 */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">メッセージを削除</DialogTitle>
            <DialogDescription className="text-slate-600">
              「{deleteTarget?.title}」を削除します。この操作は取り消せません。
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm">
              キャンセル
            </Button>
            <Button
              onClick={handleDelete}
              data-testid="confirm-delete-message"
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
            >
              削除する
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
